import { z } from 'zod';
import type { SkillKey } from './progress';
import type { ActivityContent, Catalogue } from './world-content';

// The profile's Goals tab: daily missions with a streak, a reputation level, a lifetime dream, wishes,
// courses with certificates, and perks. Wish points (WP) are earned, never bought or moved between players,
// and live in their own append-only log, like money in the ledger.

// ---------- wish points and reputation ----------
export const REP_LEVELS = [0, 50, 150, 300, 600, 1000, 1500, 2200, 3000, 4000] as const;
export const REP_TITLES = [
  'Newcomer',
  'Local',
  'Regular',
  'Familiar face',
  'Well known',
  'Respected',
  'Popular',
  'Celebrated',
  'Famous',
  'London legend',
] as const;
// reputation grows with every wish point ever earned (spending does not lower it)
export function repLevel(earned: number) {
  let level = 1;
  while (level < REP_LEVELS.length && earned >= REP_LEVELS[level]) level += 1;
  return level;
}
export const repTitle = (earned: number) => REP_TITLES[repLevel(earned) - 1];

// ---------- daily missions: three a day, the same for a life all day (UK time) ----------
export type DayAction = { kind: 'travel' | 'activity'; target: string; amountPence: number };
// missions read the published content (what an activity does, whether it is a job)
type Mission = {
  label: string;
  icon: string;
  goal: number;
  count: (actions: DayAction[], catalogue: Catalogue) => number;
};
const activityWith =
  (test: (activity: ActivityContent | undefined) => boolean) => (actions: DayAction[], catalogue: Catalogue) =>
    actions.filter((action) => action.kind === 'activity' && test(catalogue.activity(action.target))).length;
const effect = (activity: ActivityContent | undefined, need: string) =>
  (activity?.effects as Record<string, number> | undefined)?.[need] ?? 0;
export const MISSIONS = {
  work_shifts: {
    label: 'Work 2 shifts',
    icon: '💼',
    goal: 2,
    count: activityWith((activity) => Boolean(activity?.job)),
  },
  eat_out: {
    label: 'Eat out',
    icon: '🍽️',
    goal: 1,
    count: activityWith((activity) => effect(activity, 'hunger') >= 25 && activity?.placeId !== 'home'),
  },
  get_around: {
    label: 'Make 3 journeys',
    icon: '🚌',
    goal: 3,
    count: (actions) => actions.filter((a) => a.kind === 'travel').length,
  },
  visit_places: {
    label: 'Visit 3 different places',
    icon: '🗺️',
    goal: 3,
    count: (actions) => new Set(actions.filter((a) => a.kind === 'travel').map((a) => a.target)).size,
  },
  train_skills: {
    label: 'Train your skills 3 times',
    icon: '📈',
    goal: 3,
    count: activityWith((activity) => Boolean(activity?.skills && Object.keys(activity.skills).length)),
  },
  have_fun: {
    label: 'Do 2 fun things',
    icon: '🎉',
    goal: 2,
    count: activityWith((activity) => effect(activity, 'fun') >= 10),
  },
  be_social: {
    label: 'Do 2 social things',
    icon: '💬',
    goal: 2,
    count: activityWith((activity) => effect(activity, 'social') >= 10),
  },
  stay_fresh: {
    label: 'Have a shower',
    icon: '🚿',
    goal: 1,
    count: activityWith((activity) => effect(activity, 'hygiene') >= 40),
  },
} as const satisfies Record<string, Mission>;
export type MissionKey = keyof typeof MISSIONS;
export const MISSION_KEYS = Object.keys(MISSIONS) as MissionKey[];
export const MISSIONS_PER_DAY = 3;
export const MISSION_REWARD_WP = 15;
// finishing all three is a streak day: a bonus that grows with the streak, up to a week
export const allDoneBonus = (streak: number) => 10 + 5 * Math.min(Math.max(streak, 1), 7);

// a small, stable hash, so a life's missions and wishes for a day are always the same
function hash(text: string) {
  let value = 2166136261;
  for (let index = 0; index < text.length; index += 1) value = Math.imul(value ^ text.charCodeAt(index), 16777619);
  return value >>> 0;
}
function pick<T>(list: readonly T[], count: number, seed: string): T[] {
  return [...list].sort((a, b) => hash(`${seed}:${String(a)}`) - hash(`${seed}:${String(b)}`)).slice(0, count);
}
export const missionsFor = (lifeId: string, day: string) =>
  pick(MISSION_KEYS, MISSIONS_PER_DAY, `missions:${lifeId}:${day}`);
// today in London (YYYY-MM-DD)
export const ukDay = (at: Date = new Date()) =>
  new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Europe/London',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(at);

// ---------- wishes: three small wants a day, granted when the character does the thing ----------
export const WISHES: Record<string, string> = {
  flat_white: 'Fancies a flat white',
  breakfast: 'Craving a Full English',
  buy_fish: 'Wants fish and chips',
  pint: 'Could murder a pint',
  pub_quiz: 'Wants to win a pub quiz',
  karaoke: 'Wants to belt out some karaoke',
  open_mic: 'Wants to try stand-up',
  dance_class: 'Wants to learn to dance',
  photo_walk: 'Wants to take some photos',
  park_walk: 'Wants a walk in the park',
  picnic: 'Fancies a picnic',
  street_food: 'Wants street food',
  workout: 'Wants to get a sweat on',
  cook: 'Wants to cook something nice',
  watch_tv: 'Wants a night in front of the telly',
};
export const WISHES_PER_DAY = 3;
export const WISH_REWARD_WP = 10;
// only wishes for activities the published content still has
export const wishesFor = (lifeId: string, day: string, exists: (activityId: string) => boolean = () => true) =>
  pick(Object.keys(WISHES).filter(exists), WISHES_PER_DAY, `wishes:${lifeId}:${day}`);

// ---------- the lifetime dream: one at a time, changeable once a week ----------
export type DreamFacts = { careerTop: number; skillTop: number; walletPence: number; skillsAt3: number; rep: number };
export const DREAMS = {
  top_of_career: { label: 'Reach the top of a career', icon: '🏆', progress: (f: DreamFacts) => (f.careerTop - 1) / 4 },
  master_skill: { label: 'Master a skill', icon: '🎓', progress: (f: DreamFacts) => f.skillTop / 10 },
  savings: { label: 'Save £10,000', icon: '💷', progress: (f: DreamFacts) => f.walletPence / 1_000_000 },
  all_rounder: { label: 'Get every skill to level 3', icon: '🌟', progress: (f: DreamFacts) => f.skillsAt3 / 9 },
  london_legend: { label: 'Become a London legend', icon: '👑', progress: (f: DreamFacts) => (f.rep - 1) / 9 },
} as const;
export type DreamKey = keyof typeof DREAMS;
export const DREAM_KEYS = Object.keys(DREAMS) as DreamKey[];
export const DREAM_CHANGE_DAYS = 7;
export const DREAM_REWARD_WP = 250;
export const dreamProgress = (dream: DreamKey, facts: DreamFacts) =>
  Math.max(0, Math.min(1, DREAMS[dream].progress(facts)));

// ---------- courses: enrol and pay at the university, attend classes, earn a certificate ----------
// a certificate makes its skill grow 25% faster for good
// courses are content now (courses.ts); a certificate makes its skill grow faster and its job promote sooner

// ---------- perks: bought with wish points, kept for the life ----------
export const PERKS = {
  fast_learner: { label: 'Fast learner', icon: '🧠', description: 'All skills grow 10% faster.', costWp: 120 },
  power_nap: { label: 'Power nap', icon: '😴', description: 'Sleep takes half as long.', costWp: 80 },
  quick_shower: { label: 'Quick shower', icon: '🚿', description: 'Showers take half as long.', costWp: 40 },
  second_wind: { label: 'Second wind', icon: '💨', description: 'Work and workouts need 10 less energy.', costWp: 100 },
  people_person: { label: 'People person', icon: '🤗', description: 'Social things fill Social 10% more.', costWp: 80 },
  good_appetite: { label: 'Good appetite', icon: '🥘', description: 'Meals fill Hunger 10% more.', costWp: 60 },
} as const;
export type PerkKey = keyof typeof PERKS;
export const PERK_KEYS = Object.keys(PERKS) as PerkKey[];

// how perks and certificates change an activity (used by the server; shown by the app)
export function activityModifiers(
  activity: Pick<ActivityContent, 'id' | 'minEnergy' | 'job'> | undefined,
  // the skills of the courses certified, and how much faster a certificate makes its skill grow (courses.ts)
  owned: { perks: string[]; certificateSkills: string[]; certificateXpBoost: number },
) {
  const has = (perk: PerkKey) => owned.perks.includes(perk);
  const activityId = activity?.id ?? '';
  const timeFactor =
    (has('power_nap') && activityId === 'sleep') || (has('quick_shower') && /shower/.test(activityId)) ? 0.5 : 1;
  const energyRelief = has('second_wind') && activity?.minEnergy && (activity.job || activityId === 'workout') ? 10 : 0;
  const gain: Record<string, number> = {};
  if (has('people_person')) gain.social = 1.1;
  if (has('good_appetite')) gain.hunger = 1.1;
  const xp = (skill: SkillKey) =>
    (has('fast_learner') ? 1.1 : 1) * (owned.certificateSkills.includes(skill) ? 1 + owned.certificateXpBoost : 1);
  return { timeFactor, energyRelief, gain, xp };
}

// ---------- the Goals view and requests ----------
export const GoalsView = z.object({
  day: z.string(),
  wishPoints: z.number().int(),
  earnedWishPoints: z.number().int(),
  missions: z.array(
    z.object({
      key: z.string(),
      label: z.string(),
      icon: z.string(),
      done: z.number().int(),
      goal: z.number().int(),
      claimed: z.boolean(),
    }),
  ),
  streak: z.number().int(),
  wishes: z.array(z.object({ activityId: z.string(), label: z.string(), granted: z.boolean() })),
  dream: z
    .object({
      key: z.string(),
      progress: z.number(),
      achieved: z.boolean(),
      claimed: z.boolean(),
      changeFrom: z.string().nullable(),
    })
    .nullable(),
  perks: z.array(z.string()),
  // the courses certified (courses.ts)
  certificates: z.array(z.string()),
});
export type GoalsView = z.infer<typeof GoalsView>;
export const GoalsReply = z.object({ goals: GoalsView, message: z.string().optional() });
export type GoalsReply = z.infer<typeof GoalsReply>;
export const DreamChoice = z.object({ dream: z.enum(DREAM_KEYS as [DreamKey, ...DreamKey[]]) }).strict();
export type DreamChoice = z.infer<typeof DreamChoice>;
export const MissionClaim = z.object({ mission: z.enum(MISSION_KEYS as [MissionKey, ...MissionKey[]]) }).strict();
export const PerkBuy = z.object({ perk: z.enum(PERK_KEYS as [PerkKey, ...PerkKey[]]) }).strict();
